import {
  X_CLIENT_HEADER_NAME,
  X_CLIENT_HEADER_VALUE,
} from "@/lib/clientHeader";
import { webApiClient } from "@/lib/queryClient";
import { logger } from "@/lib/logger";

interface User {
  id: string;
  canonicalId?: string;
  email: string;
  name: string;
  avatar?: string;
  bio?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  kudozBalance?: number;
  lifetimeKudozEarned?: number;
  accountBalance?: string;
  trustScore?: number;
  trustTier?: "newbie" | "reliable" | "trusted" | "elite";
  totalReviews?: number;
  totalOrders?: number;
  profileCompletionPercent?: number;
  followersCount?: number;
  followingCount?: number;
  isGuestAccount?: boolean;
}

interface AuthResponse {
  token: string;
  user: User;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export class AuthService {
  private readonly TOKEN_KEY = "auth_token";
  private readonly USER_KEY = "auth_user";
  private readonly USER_CACHE_KEY = "user_profile_cache";
  private readonly EATZY_CACHE_KEY = "eatzy_profile_cache";
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly EATZY_REFRESH_TOKEN_KEY = "eatzyRefreshToken";
  private readonly EATZY_TOKEN_EXPIRES_KEY = "eatzyTokenExpires";
  private readonly EATZY_REFRESH_EXPIRES_KEY = "eatzyRefreshExpires";

  /**
   * Extract error message from API response
   */
  private async extractErrorMessage(
    response: Response,
    defaultMessage: string
  ): Promise<string> {
    try {
      const error = await response.json();
      logger.log("[AuthService] Error response:", JSON.stringify(error));

      // Handle different error response formats from Eatzy auth
      // The API may return { code: "INVALID_EMAIL_OR_PASSWORD" } or { error: "..." } or { message: "..." }
      let errorMessage = error.message || error.error || defaultMessage;

      // Translate error codes to user-friendly messages
      if (error.code === "INVALID_EMAIL_OR_PASSWORD") {
        errorMessage =
          "Invalid email or password. Please check your credentials and try again.";
      } else if (error.code) {
        // Use the code if no better message is available
        errorMessage = error.code.replace(/_/g, " ").toLowerCase();
      }

      return errorMessage;
    } catch (parseError) {
      // If response is not JSON, use status text or generic message
      logger.warn("[AuthService] Failed to parse error response:", parseError);
      return response.statusText || `${defaultMessage} (${response.status})`;
    }
  }

  /**
   * Get current auth state from localStorage
   */
  getAuthState(): AuthState {
    let token = localStorage.getItem(this.TOKEN_KEY);
    if (token === "undefined" || token === "null" || token === "") {
      localStorage.removeItem(this.TOKEN_KEY);
      token = null;
    }
    const userStr = localStorage.getItem(this.USER_KEY);

    let user: User | null = null;
    if (userStr && userStr !== "undefined" && userStr !== "null") {
      try {
        user = JSON.parse(userStr);
        // Validate parsed user has required fields
        if (!user || typeof user !== "object" || !user.id || !user.email) {
          logger.warn("⚠️ Stored user is missing required fields, clearing");
          user = null;
          this.clearAuth();
        }
      } catch (error) {
        logger.error("Error parsing stored user:", error);
        localStorage.removeItem(this.USER_KEY);
      }
    } else if (userStr === "undefined" || userStr === "null") {
      // Clean up invalid stored values
      logger.warn("⚠️ Cleaning up invalid stored user value:", userStr);
      localStorage.removeItem(this.USER_KEY);
    }

    return {
      user,
      token,
      isAuthenticated: !!(token && user),
      isLoading: false,
    };
  }

  /**
   * Sign up with email and password
   */
  async signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    inviteAttribution?: { inviteToken?: string }
  ): Promise<AuthResponse> {
    // Call backend auth route which handles proxying to Eatzy
    const name = `${firstName} ${lastName}`.trim();
    const response = await fetch("/api/auth/sign-up", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
      },
      body: JSON.stringify({
        email,
        password,
        name,
        firstName,
        lastName,
        // Task #840 — partner-invite signups forward the raw invite token
        // (NOT a derived inviter id). The server hashes it, validates the
        // invite is still pending, and looks up the inviter's Eatzy
        // user_id to send as `invited_by` to the Auth Hub. This prevents
        // attribution spoofing from arbitrary clients.
        ...(inviteAttribution?.inviteToken && {
          inviteToken: inviteAttribution.inviteToken,
        }),
      }),
    });

    if (!response.ok) {
      const errorMessage = await this.extractErrorMessage(
        response,
        "Sign up failed"
      );
      throw new Error(errorMessage);
    }

    const data = await response.json();

    logger.log("[AuthService] Sign up response:", JSON.stringify(data));

    // Store eatzyAccessToken if the backend returned one (mirrors sign-in behaviour).
    // This allows Kudoz wallet sync to work immediately after sign-up.
    if (data.eatzyAccessToken) {
      sessionStorage.setItem(
        "adminEatzyToken",
        data.eatzyAccessToken as string
      );
      sessionStorage.setItem(
        "partnerEatzyToken",
        data.eatzyAccessToken as string
      );
      localStorage.setItem("eatzyUserToken", data.eatzyAccessToken as string);
      if (data.eatzyRefreshToken)
        localStorage.setItem(
          this.EATZY_REFRESH_TOKEN_KEY,
          data.eatzyRefreshToken as string
        );
      if (data.eatzyTokenExpires)
        localStorage.setItem(
          this.EATZY_TOKEN_EXPIRES_KEY,
          String(data.eatzyTokenExpires)
        );
    }

    // Fetch full user profile from backend to get kudoz balance
    try {
      // Validate token before fetching profile
      if (!data.token || typeof data.token !== "string") {
        logger.warn("Invalid token received during sign up");
        return data as unknown as AuthResponse;
      }

      const fullUser = await this.fetchUserProfile(data.token as string);

      const enrichedUser = {
        ...(data.user as Record<string, unknown>),
        ...fullUser,
      };
      this.setAuth(data.token as string, enrichedUser as unknown as User);
      return { ...data, user: enrichedUser } as unknown as AuthResponse;
    } catch (error) {
      // If profile fetch fails, continue with basic user data
      logger.warn("Failed to fetch full user profile:", error);
      this.setAuth(data.token as string, data.user as unknown as User);
      return data as unknown as AuthResponse;
    }
  }

  /**
   * Get current session from server (cookie-based)
   */
  async getSession(): Promise<{
    user: User;
    session: Record<string, unknown>;
  } | null> {
    try {
      const response = await fetch("/api/auth/session", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
        },
        credentials: "include", // Important: Include cookies
      });

      if (!response.ok) {
        logger.warn(
          `[AuthService] getSession failed: ${response.status} ${response.statusText}`
        );
        const text = await response.text();
        logger.warn(`[AuthService] Response body: ${text}`);
        return null;
      }

      const data = await response.json();
      return data as unknown as {
        user: User;
        session: Record<string, unknown>;
      };
    } catch (error) {
      logger.error("Get session error:", error);
      return null;
    }
  }

  /**
   * Sign in with Google OAuth
   * Uses server-side redirect flow to ensure secrets stay server-side
   */
  signInWithGoogle(): void {
    logger.log("🔧 Initiating Google Sign In via server redirect...");
    window.location.href = "/api/auth/google";
  }

  /**
   * Sign in with Facebook OAuth
   */
  signInWithFacebook(): void {
    window.location.href = "/api/auth/facebook";
  }

  /**
   * Sign in with Apple OAuth
   */
  signInWithApple(): void {
    window.location.href = "/api/auth/apple";
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    // Clear any existing auth data before attempting login
    // This prevents old session cookies from interfering with new login attempts
    this.clearAllAuthData();
    logger.log("[AuthService] Cleared existing auth data before login attempt");

    // Call backend auth route which handles password hashing internally
    const response = await fetch("/api/auth/sign-in", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
      },
      body: JSON.stringify({ email, password }),
    });

    let data: Record<string, unknown> | null = null;
    try {
      data = (await response.json()) as Record<string, unknown>;
    } catch {
      // data stays null from initialization
    }

    if (
      !response.ok ||
      !data ||
      data.error ||
      data.code ||
      data.success === false
    ) {
      const errorMessage =
        data?.message || data?.error || data?.code || "Sign in failed";
      throw new Error(String(errorMessage));
    }

    if (data.eatzyAccessToken) {
      sessionStorage.setItem(
        "adminEatzyToken",
        data.eatzyAccessToken as string
      );
      sessionStorage.setItem(
        "partnerEatzyToken",
        data.eatzyAccessToken as string
      );
      localStorage.setItem("eatzyUserToken", data.eatzyAccessToken as string);
      if (data.eatzyRefreshToken)
        localStorage.setItem(
          this.EATZY_REFRESH_TOKEN_KEY,
          data.eatzyRefreshToken as string
        );
      if (data.eatzyTokenExpires)
        localStorage.setItem(
          this.EATZY_TOKEN_EXPIRES_KEY,
          String(data.eatzyTokenExpires)
        );
    }

    // Fetch full user profile from backend to get kudoz balance
    try {
      const fullUser = await this.fetchUserProfile(data.token as string);
      const enrichedUser = {
        ...(data.user as Record<string, unknown>),
        ...fullUser,
      };
      this.setAuth(data.token as string, enrichedUser as unknown as User);
      return { ...data, user: enrichedUser } as unknown as AuthResponse;
    } catch (error) {
      // If profile fetch fails, continue with basic user data
      logger.warn("Failed to fetch full user profile:", error);
      this.setAuth(data.token as string, data.user as unknown as User);
      return data as unknown as AuthResponse;
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      await fetch("/api/auth/sign-out", {
        method: "POST",
        headers,
      });
    } catch (error) {
      logger.error("Sign out error:", error);
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Verify current token
   */
  async verifyToken(token?: string): Promise<User | null> {
    try {
      const rawToken = token || localStorage.getItem(this.TOKEN_KEY);
      const localToken =
        rawToken && rawToken !== "undefined" && rawToken !== "null"
          ? rawToken
          : null;
      if (localToken) {
        logger.log("🔑 Using localStorage token for verification");
        const response = await fetch("/api/auth/verify", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
          },
          body: JSON.stringify({ token: localToken }),
        });

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        if (!data.valid) {
          return null;
        }

        // Fetch full user profile using token-based auth
        try {
          const fullUser = await this.fetchUserProfile(localToken);
          return { ...data.user, ...fullUser };
        } catch (error) {
          // If profile fetch fails, return basic user data
          logger.warn(
            "Failed to fetch full user profile during verification:",
            error
          );
          return data.user;
        }
      }
      return null;
    } catch (error) {
      logger.error("Token verification error:", error);
      return null;
    }
  }

  /**
   * Get auth headers for API calls
   */
  getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (token && token !== "undefined" && token !== "null") {
      logger.log("🔑 Using localStorage token for Authorization header");
      return { Authorization: `Bearer ${token}` };
    }

    return {};
  }

  /**
   * Fetch full user profile from backend
   */
  async fetchUserProfile(token: string): Promise<Partial<User>> {
    try {
      const sanitizedToken =
        token && token !== "undefined" && token !== "null" ? token : null;
      const response = await fetch("/api/user", {
        method: "GET",
        headers: sanitizedToken
          ? {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sanitizedToken}`,
              [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
            }
          : {
              "Content-Type": "application/json",
              [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE,
            },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user profile");
      }

      const userData = await response.json();
      return {
        bio: userData.bio || undefined,
        displayName: userData.displayName || undefined,
        firstName: userData.firstName || undefined,
        lastName: userData.lastName || undefined,
        phone: userData.phone || undefined,
        avatar: userData.avatar || undefined,
        kudozBalance: userData.kudozBalance || 0,
        lifetimeKudozEarned: userData.lifetimeKudozEarned || 0,
        accountBalance: userData.accountBalance || "0",
        trustScore: userData.trustScore ?? undefined,
        trustTier: userData.trustTier ?? undefined,
        totalReviews: userData.totalReviews ?? 0,
        totalOrders: userData.totalOrders ?? 0,
        profileCompletionPercent:
          userData.profileCompletionPercent ?? undefined,
      };
    } catch (error) {
      logger.error("Error fetching user profile:", error);
      throw error;
    }
  }

  /**
   * Make authenticated API call to Kudoz backend
   */
  async apiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const headers = {
      "Content-Type": "application/json",
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    return fetch(`/api${endpoint}`, {
      ...options,
      headers,
    });
  }

  /**
   * Silently refresh the Eatzy access token. Delegates to the typed
   * `@kudoz/api-client` (`webApiClient.auth.refreshEatzyToken()`), which
   * owns the single in-flight Promise (shared with the queryClient
   * refresh-and-retry path) and persists the new access token + expiry
   * through `createWebStorage` — including the `adminEatzyToken` /
   * `partnerEatzyToken` sessionStorage mirrors.
   * Returns true on success, false otherwise.
   */
  async refreshEatzyToken(): Promise<boolean> {
    // Delegate to the typed `@kudoz/api-client`. The client owns the
    // single in-flight Promise (shared with the queryClient refresh path),
    // performs the POST /api/auth/refresh-eatzy-token call, and writes the
    // new access token + expiry through `createWebStorage` (which also
    // mirrors into `sessionStorage.adminEatzyToken` / `partnerEatzyToken`).
    try {
      const ok = await webApiClient.auth.refreshEatzyToken();
      if (ok) {
        logger.log("[AuthService] Eatzy access token silently refreshed");
      } else {
        logger.warn("[AuthService] Eatzy token refresh failed");
      }
      return ok;
    } catch (error) {
      logger.error("[AuthService] Error during Eatzy token refresh:", error);
      return false;
    }
  }

  /**
   * Silently refresh the Kudoz JWT (auth_token). Delegates to the typed
   * `@kudoz/api-client` (`webApiClient.auth.refreshKudozToken()`), which
   * owns the single in-flight Promise (shared with the queryClient
   * refresh-and-retry path) and writes the new JWT through
   * `createWebStorage`.
   * Returns true on success, false if the JWT is too stale to refresh or
   * any error occurred.
   *
   * Note: the typed client only persists the new token; it does NOT merge
   * any `user` payload returned by the refresh endpoint back into the
   * stored `auth_user`. No caller depends on that behaviour today —
   * `useAuth` re-fetches the profile via `verifyToken` / `fetchUserProfile`.
   */
  async refreshKudozToken(): Promise<boolean> {
    // Delegate to the typed `@kudoz/api-client`. The client owns the
    // single in-flight Promise (shared with the queryClient refresh path),
    // performs the POST /api/auth/refresh-kudoz-token call with the current
    // Bearer credential, and writes the new JWT through `createWebStorage`.
    try {
      const ok = await webApiClient.auth.refreshKudozToken();
      if (ok) {
        logger.log("[AuthService] Kudoz JWT silently refreshed");
      } else {
        logger.warn("[AuthService] Kudoz token refresh failed");
      }
      return ok;
    } catch (error) {
      logger.error("[AuthService] Error during Kudoz token refresh:", error);
      return false;
    }
  }

  /**
   * Returns seconds until the stored Eatzy access token expires, or null if unknown.
   */
  getEatzyTokenSecondsUntilExpiry(): number | null {
    const expiresStr = localStorage.getItem(this.EATZY_TOKEN_EXPIRES_KEY);
    if (!expiresStr) return null;
    const expiresAt = Number(expiresStr);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return null;
    return expiresAt - Math.floor(Date.now() / 1000);
  }

  hasEatzyRefreshToken(): boolean {
    return !!localStorage.getItem(this.EATZY_REFRESH_TOKEN_KEY);
  }

  async eatzyApiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Proactive refresh: if the stored Eatzy token expires within 60 s, refresh it first
    const expiresStr = localStorage.getItem(this.EATZY_TOKEN_EXPIRES_KEY);
    if (expiresStr) {
      const expiresAt = Number(expiresStr);
      const nowSec = Math.floor(Date.now() / 1000);
      if (expiresAt > 0 && expiresAt - nowSec < 60) {
        logger.log(
          "[AuthService] Eatzy token expiring soon, proactively refreshing..."
        );
        await this.refreshEatzyToken();
      }
    }

    const makeCall = async (): Promise<Response> => {
      const eatzyToken = localStorage.getItem("eatzyUserToken");
      const headers = {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(eatzyToken && { "X-User-Eatzy-Token": eatzyToken }),
        ...options.headers,
      };
      const proxyEndpoint = `/api/eatzy${endpoint}`;
      const { cache, ...restOptions } = options;
      logger.log("🔑 Using backend proxy for Eatzy API call:", proxyEndpoint);
      return fetch(proxyEndpoint, {
        cache: cache ?? "no-store",
        ...restOptions,
        headers,
      });
    };

    const response = await makeCall();

    // Reactive refresh: if the proxy signals eatzyTokenExpired, try once then retry.
    // Covers both 401 and 403 since Eatzy uses 403 for expired tokens but some
    // proxy paths may surface a 401 instead.
    if (response.status === 401 || response.status === 403) {
      try {
        const cloned = response.clone();
        const errorData = await cloned.json().catch(() => ({}));
        if (errorData.eatzyTokenExpired) {
          logger.log(
            "[AuthService] Eatzy token expired (proxy signal), attempting silent refresh..."
          );
          const refreshed = await this.refreshEatzyToken();
          if (refreshed) {
            logger.log(
              "[AuthService] Eatzy token refreshed, retrying request..."
            );
            return makeCall();
          }
          // refreshEatzyToken returned false — no refresh token stored (consumer accounts
          // never receive one from /account/authorize). Clear the stale Eatzy token so
          // subsequent calls go directly to the Kudoz JWT path on the server, avoiding a
          // wasted round-trip with a token that the server already knows is expired.
          const refreshToken = localStorage.getItem(
            this.EATZY_REFRESH_TOKEN_KEY
          );
          if (!refreshToken) {
            logger.warn(
              "[AuthService] No refresh token — clearing stale Eatzy user token to skip future 403 retries"
            );
            localStorage.removeItem("eatzyUserToken");
            localStorage.removeItem(this.EATZY_TOKEN_EXPIRES_KEY);
          }
          // If the refresh token was expired/invalid, refreshEatzyToken() will have already
          // triggered signOut + redirect. Just return the 403 to let callers surface an
          // EatzyTokenExpiredError UI.
        }
      } catch {
        // If response parsing fails, return the original response as-is
      }
    }

    return response;
  }

  /**
   * Store auth data
   */
  private setAuth(token: string, user: User): void {
    // Only store if both token and user are defined
    if (token && user) {
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } else {
      logger.warn(
        "⚠️ setAuth called with invalid data - token:",
        !!token,
        "user:",
        !!user
      );
    }
  }

  /**
   * Update stored user data (used to sync enriched user data after verification)
   */
  updateStoredUser(user: User): void {
    if (user && user.id && user.email) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      logger.log("💾 Updated stored user data with enriched fields");
    }
  }

  /**
   * Get cached user profile with TTL check
   */
  getCachedUserProfile(): Record<string, unknown> | null {
    try {
      const cached = localStorage.getItem(this.USER_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > this.CACHE_TTL;

      if (isExpired) {
        localStorage.removeItem(this.USER_CACHE_KEY);
        return null;
      }

      logger.log("📦 Using cached user profile");
      return data as unknown as Record<string, unknown>;
    } catch (error) {
      logger.error("Error reading cached user profile:", error);
      localStorage.removeItem(this.USER_CACHE_KEY);
      return null;
    }
  }

  /**
   * Cache user profile with timestamp
   */
  setCachedUserProfile(userData: Record<string, unknown>): void {
    try {
      const cacheData = {
        data: userData,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.USER_CACHE_KEY, JSON.stringify(cacheData));
      logger.log("💾 Cached user profile");
    } catch (error) {
      logger.error("Error caching user profile:", error);
    }
  }

  /**
   * Get cached Eatzy profile
   */
  getCachedEatzyProfile(): Record<string, unknown> | null {
    try {
      const cached = localStorage.getItem(this.EATZY_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > this.CACHE_TTL;

      if (isExpired) {
        localStorage.removeItem(this.EATZY_CACHE_KEY);
        return null;
      }

      logger.log("📦 Using cached Eatzy profile");
      return data as unknown as Record<string, unknown>;
    } catch (error) {
      logger.error("Error reading cached Eatzy profile:", error);
      localStorage.removeItem(this.EATZY_CACHE_KEY);
      return null;
    }
  }

  /**
   * Cache Eatzy profile
   */
  setCachedEatzyProfile(eatzyData: Record<string, unknown>): void {
    try {
      const cacheData = {
        data: eatzyData,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.EATZY_CACHE_KEY, JSON.stringify(cacheData));
      logger.log("💾 Cached Eatzy profile");
    } catch (error) {
      logger.error("Error caching Eatzy profile:", error);
    }
  }

  /**
   * Get user profile with smart caching
   */
  async getUserProfile(forceRefresh: boolean = false): Promise<unknown> {
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.getCachedUserProfile();
      if (cached) {
        return cached;
      }
    }

    logger.log("🔄 Fetching fresh user profile...");

    try {
      // Fetch from server
      const response = await this.apiCall("/user");

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.status}`);
      }

      const userData = await response.json();

      // Cache the result
      this.setCachedUserProfile(userData);

      return userData;
    } catch (error) {
      logger.error("Error fetching user profile:", error);

      // Fallback to stale cache if available
      const staleCache = localStorage.getItem(this.USER_CACHE_KEY);
      if (staleCache) {
        logger.log("📦 Using stale cache as fallback");
        const { data } = JSON.parse(staleCache);
        return data as unknown as Record<string, unknown>;
      }

      throw error;
    }
  }

  /**
   * Get Eatzy profile with smart caching
   */
  async getEatzyProfile(forceRefresh: boolean = false): Promise<unknown> {
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.getCachedEatzyProfile();
      if (cached) {
        return cached;
      }
    }

    logger.log("🔄 Fetching fresh Eatzy profile...");

    try {
      const response = await this.eatzyApiCall("/account/profile");

      if (!response.ok) {
        throw new Error(`Failed to fetch Eatzy profile: ${response.status}`);
      }

      const eatzyData = await response.json();

      // Cache the result
      this.setCachedEatzyProfile(eatzyData);

      return eatzyData;
    } catch (error) {
      logger.error("Error fetching Eatzy profile:", error);

      // Fallback to stale cache if available
      const staleCache = localStorage.getItem(this.EATZY_CACHE_KEY);
      if (staleCache) {
        logger.log("📦 Using stale Eatzy cache as fallback");
        const { data } = JSON.parse(staleCache);
        return data as unknown as Record<string, unknown>;
      }

      throw error;
    }
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    localStorage.removeItem(this.USER_CACHE_KEY);
    localStorage.removeItem(this.EATZY_CACHE_KEY);
    localStorage.removeItem("user_addresses_cache");
    localStorage.removeItem("eatzyUserToken");
    localStorage.removeItem(this.EATZY_REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.EATZY_TOKEN_EXPIRES_KEY);
    localStorage.removeItem(this.EATZY_REFRESH_EXPIRES_KEY);
    localStorage.removeItem("__kudoz_refresh_token__");
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("user_addresses_cache_")) {
        localStorage.removeItem(key);
      }
    });
    logger.log("🗑️ Cleared all caches");
  }

  /**
   * Clear auth data
   */
  private clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.clearAllCaches(); // Also clear caches on logout
  }

  /**
   * Clear all auth data (for debugging)
   */
  clearAllAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.clearAllCaches();
    logger.log("🗑️ Cleared all auth data");
  }
}

export const authService = new AuthService();
export type { User, AuthState, AuthResponse };
